/**
 * UnifiedSettingsSheet - Panneau de paramètres unifié avec onglets
 * Combine tous les paramètres en une seule interface cohérente
 */

import React, { memo, useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Settings, Clock, Zap, Database,
  Bell, BellOff, AlertCircle, Shield, Download,
  LogOut, Cloud, CloudOff, RefreshCw, Moon, Sun,
  MapPin, Utensils, Briefcase,
  Music, Youtube, Tv, Sparkles, Trophy,
  Calendar, Target, Award, RotateCcw,
  CheckSquare, ChevronDown, Bug, Copy, Trash2, CheckCheck,
  Eye, Volume2, VolumeX, Mic,
} from 'lucide-react';
import { getLastErrors, clearErrors, subscribe } from '../../../services/errorService';
import { BottomSheet } from '../../../components/ui/BottomSheet';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAdvancedSettings } from '../../../hooks/useAdvancedSettings';
import { useAccessibility } from '../../../hooks/useAccessibility';

// Tab configuration
const TABS = [
  { key: 'general',  label: 'Général',  icon: Settings  },
  { key: 'schedule', label: 'Horaires', icon: Clock     },
  { key: 'services', label: 'Services', icon: Zap       },
  { key: 'data',     label: 'Données',  icon: Database  },
  { key: 'a11y',     label: 'A11y',     icon: Eye       },
  { key: 'diag',     label: 'Diag.',    icon: Bug       },
];

/**
 * Main Settings Sheet Component
 */
export const UnifiedSettingsSheet = memo(function UnifiedSettingsSheet({
  isOpen,
  onClose,
  // General settings props
  settings: appSettings,
  onToggleSetting,
  onExportCSV,
  onSignOut,
  onRefreshCalendar,
  onSyncGoogleTasks,
  isLocalMode,
  isGoogleConnected,
  isTasksAvailable,
  isSyncing,
  // Week generation props
  onGenerateWeek,
  existingTasks = {},
  // Reset props
  onResetTasks,
  onResetAgendaSetup,
  // TTS props
  ttsSettings,
  onUpdateTTS,
  ttsVoices,
}) {
  const [activeTab, setActiveTab] = useState('general');
  const { theme } = useTheme();
  const advancedSettings = useAdvancedSettings();
  const a11y = useAccessibility();

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Paramètres"
      size="full"
    >
      <div className="flex flex-col h-full overflow-hidden">
        {/* Tab Navigation */}
        <div className="flex border-b border-zinc-800 px-2 bg-zinc-900/50 sticky top-0 z-10">
          {TABS.map(tab => {
            const TabIcon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`flex-1 flex flex-col items-center gap-1 py-3 px-2
                  transition-all relative ${
                  isActive
                    ? 'text-white'
                    : 'text-zinc-500 hover:text-zinc-300'
                }`}
              >
                <TabIcon className="w-5 h-5" />
                <span className="text-[10px] font-medium">{tab.label}</span>
                {isActive && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full"
                    style={{ backgroundColor: theme.colors.accent }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto pb-safe">
          <AnimatePresence mode="wait">
            {activeTab === 'general' && (
              <TabContent key="general">
                <GeneralTab
                  theme={theme}
                  appSettings={appSettings}
                  onToggleSetting={onToggleSetting}
                  isLocalMode={isLocalMode}
                  isGoogleConnected={isGoogleConnected}
                  isSyncing={isSyncing}
                  onRefreshCalendar={onRefreshCalendar}
                  onSignOut={onSignOut}
                  ttsSettings={ttsSettings}
                  onUpdateTTS={onUpdateTTS}
                  ttsVoices={ttsVoices}
                />
              </TabContent>
            )}

            {activeTab === 'schedule' && (
              <TabContent key="schedule">
                <ScheduleTab
                  settings={advancedSettings.settings}
                  updateSetting={advancedSettings.updateSetting}
                />
              </TabContent>
            )}

            {activeTab === 'services' && (
              <TabContent key="services">
                <ServicesTab
                  settings={advancedSettings.settings}
                  updateSetting={advancedSettings.updateSetting}
                />
              </TabContent>
            )}

            {activeTab === 'data' && (
              <TabContent key="data">
                <DataTab
                  settings={advancedSettings.settings}
                  updateSetting={advancedSettings.updateSetting}
                  onExportCSV={onExportCSV}
                  onSyncGoogleTasks={onSyncGoogleTasks}
                  isGoogleConnected={isGoogleConnected}
                  isTasksAvailable={isTasksAvailable}
                  isSyncing={isSyncing}
                  onGenerateWeek={onGenerateWeek}
                  existingTasks={existingTasks}
                  resetAll={advancedSettings.resetAll}
                  onResetTasks={onResetTasks}
                  onResetAgendaSetup={onResetAgendaSetup}
                />
              </TabContent>
            )}

            {activeTab === 'a11y' && (
              <TabContent key="a11y">
                <A11yTab a11y={a11y} />
              </TabContent>
            )}

            {activeTab === 'diag' && (
              <TabContent key="diag">
                <DiagTab />
              </TabContent>
            )}
          </AnimatePresence>
        </div>
      </div>
    </BottomSheet>
  );
});

/**
 * Tab Content Wrapper with animations
 */
const TabContent = memo(function TabContent({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
      className="p-4 space-y-4"
    >
      {children}
    </motion.div>
  );
});

/**
 * General Tab - Theme, notifications, connection
 */
const GeneralTab = memo(function GeneralTab({
  theme,
  appSettings,
  onToggleSetting,
  isLocalMode,
  isGoogleConnected,
  isSyncing,
  onRefreshCalendar,
  onSignOut,
  ttsSettings,
  onUpdateTTS,
  ttsVoices,
}) {
  return (
    <>
      {/* Theme Display */}
      <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-xl">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: theme.colors.accentGlow }}
          >
            {theme.key === 'night' || theme.key === 'evening' ? (
              <Moon className="w-5 h-5" style={{ color: theme.colors.accent }} />
            ) : (
              <Sun className="w-5 h-5" style={{ color: theme.colors.accent }} />
            )}
          </div>
          <div>
            <span className="text-sm font-medium text-zinc-200">Thème {theme.name}</span>
            <span className="text-xs text-zinc-500 block">Adapté automatiquement</span>
          </div>
        </div>
        <div
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: theme.colors.accent }}
        />
      </div>

      {/* Connection Status */}
      <div className="p-3 bg-zinc-800/30 rounded-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isLocalMode ? (
              <CloudOff className="w-5 h-5 text-zinc-500" />
            ) : isGoogleConnected ? (
              <Cloud className="w-5 h-5 text-emerald-500" />
            ) : (
              <Cloud className="w-5 h-5 text-amber-500" />
            )}
            <div>
              <span className="text-sm font-medium text-zinc-200">
                {isLocalMode ? 'Mode hors-ligne' : isGoogleConnected ? 'Google connecté' : 'Non connecté'}
              </span>
              <span className="text-xs text-zinc-500 block">
                {isLocalMode ? 'Données locales' : 'Sync calendrier'}
              </span>
            </div>
          </div>
          {!isLocalMode && isGoogleConnected && (
            <button
              onClick={onRefreshCalendar}
              disabled={isSyncing}
              className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700/50 rounded-lg transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {/* Toggles */}
      <div className="space-y-2">
        <SettingToggle
          icon={appSettings.notificationsEnabled ? Bell : BellOff}
          iconActive={appSettings.notificationsEnabled}
          label="Notifications"
          description="Rappels de tâches"
          checked={appSettings.notificationsEnabled}
          onChange={() => {
            if (!appSettings.notificationsEnabled && 'Notification' in window) {
              Notification.requestPermission();
            }
            onToggleSetting('notificationsEnabled');
          }}
        />

        {/* TTS — synthèse vocale */}
        {ttsSettings && onUpdateTTS && (
          <div className="rounded-xl bg-zinc-800/50 border border-zinc-700/50 overflow-hidden">
            <SettingToggle
              icon={ttsSettings.enabled ? Volume2 : VolumeX}
              iconActive={ttsSettings.enabled}
              label="Annonces vocales"
              description="Lit les rappels à voix haute (ElevenLabs)"
              checked={ttsSettings.enabled}
              onChange={() => onUpdateTTS({ enabled: !ttsSettings.enabled })}
            />

            {ttsSettings.enabled && (
              <div className="px-4 pb-4 space-y-3 border-t border-zinc-700/40 pt-3">
                {/* Sélection voix */}
                <div className="flex items-center gap-2">
                  <Mic className="w-4 h-4 text-zinc-400 shrink-0" />
                  <span className="text-xs text-zinc-400 w-16 shrink-0">Voix</span>
                  <div className="flex gap-2 flex-1">
                    {ttsVoices && Object.entries(ttsVoices).map(([key, voice]) => (
                      <button
                        key={key}
                        onClick={() => onUpdateTTS({ gender: key })}
                        className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-medium transition-all ${
                          ttsSettings.gender === key
                            ? 'bg-violet-600 text-white'
                            : 'bg-zinc-700/60 text-zinc-400 hover:text-white hover:bg-zinc-700'
                        }`}
                      >
                        {key === 'female' ? '👩 ' : '👨 '}{voice.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Volume */}
                <div className="flex items-center gap-2">
                  <Volume2 className="w-4 h-4 text-zinc-400 shrink-0" />
                  <span className="text-xs text-zinc-400 w-16 shrink-0">Volume</span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={ttsSettings.volume ?? 0.9}
                    onChange={e => onUpdateTTS({ volume: parseFloat(e.target.value) })}
                    className="flex-1 accent-violet-500"
                  />
                  <span className="text-xs text-zinc-500 w-8 text-right">
                    {Math.round((ttsSettings.volume ?? 0.9) * 100)}%
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        <SettingToggle
          icon={AlertCircle}
          iconActive={appSettings.safetyMode}
          label="Mode sécurité"
          description="Affiche uniquement les tâches vitales"
          checked={appSettings.safetyMode}
          onChange={() => onToggleSetting('safetyMode')}
        />
      </div>

      {/* Sign Out */}
      {!isLocalMode && isGoogleConnected && (
        <button
          onClick={onSignOut}
          className="w-full flex items-center gap-3 p-3 bg-red-950/30 hover:bg-red-900/30 rounded-xl transition-all active:scale-[0.98]"
        >
          <LogOut className="w-5 h-5 text-red-400" />
          <div className="text-left">
            <span className="text-sm font-medium text-red-300 block">Déconnexion</span>
            <span className="text-xs text-red-400/60">Google compte</span>
          </div>
        </button>
      )}

      {/* Privacy Note */}
      <div className="pt-2 text-center">
        <div className="text-xs text-zinc-600 font-medium flex items-center justify-center gap-1">
          <Shield className="w-3.5 h-3.5" />
          Données stockées localement
        </div>
      </div>
    </>
  );
});

/**
 * Schedule Tab - Times and address
 */
const ScheduleTab = memo(function ScheduleTab({ settings, updateSetting }) {
  return (
    <>
      {/* Address Section */}
      <CollapsibleSection
        icon={MapPin}
        iconColor="text-blue-400"
        title="Mon adresse"
        subtitle={`${settings.address.street}, ${settings.address.city}`}
        defaultOpen={false}
      >
        <div className="space-y-3 mt-3">
          <InputField
            label="Rue"
            value={settings.address.street}
            onChange={(v) => updateSetting('address.street', v)}
            placeholder="46 bis Avenue Jean Jaurès"
          />
          <div className="grid grid-cols-2 gap-3">
            <InputField
              label="Code postal"
              value={settings.address.postalCode}
              onChange={(v) => updateSetting('address.postalCode', v)}
              placeholder="75019"
            />
            <InputField
              label="Ville"
              value={settings.address.city}
              onChange={(v) => updateSetting('address.city', v)}
              placeholder="Paris"
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* Wake/Sleep Times */}
      <CollapsibleSection
        icon={Clock}
        iconColor="text-amber-400"
        title="Réveil & Coucher"
        subtitle={`${settings.schedule.wakeTime} - ${settings.schedule.sleepTime}`}
        defaultOpen={true}
      >
        <div className="grid grid-cols-2 gap-3 mt-3">
          <TimeField
            icon={Sun}
            iconColor="text-amber-400"
            label="Réveil"
            value={settings.schedule.wakeTime}
            onChange={(v) => updateSetting('schedule.wakeTime', v)}
          />
          <TimeField
            icon={Moon}
            iconColor="text-indigo-400"
            label="Coucher"
            value={settings.schedule.sleepTime}
            onChange={(v) => updateSetting('schedule.sleepTime', v)}
          />
        </div>
      </CollapsibleSection>

      {/* Meals */}
      <CollapsibleSection
        icon={Utensils}
        iconColor="text-orange-400"
        title="Repas"
        subtitle={`Déj ${settings.schedule.lunchTime} - Dîner ${settings.schedule.dinnerTime}`}
        defaultOpen={false}
      >
        <div className="grid grid-cols-3 gap-2 mt-3">
          <TimeField
            label="Petit-déj"
            value={settings.schedule.breakfastTime}
            onChange={(v) => updateSetting('schedule.breakfastTime', v)}
            compact
          />
          <TimeField
            label="Déjeuner"
            value={settings.schedule.lunchTime}
            onChange={(v) => updateSetting('schedule.lunchTime', v)}
            compact
          />
          <TimeField
            label="Dîner"
            value={settings.schedule.dinnerTime}
            onChange={(v) => updateSetting('schedule.dinnerTime', v)}
            compact
          />
        </div>
      </CollapsibleSection>

      {/* Work Hours */}
      <CollapsibleSection
        icon={Briefcase}
        iconColor="text-purple-400"
        title="Travail"
        subtitle={`${settings.schedule.workStartTime} - ${settings.schedule.workEndTime}`}
        defaultOpen={false}
      >
        <div className="grid grid-cols-2 gap-3 mt-3">
          <TimeField
            label="Début"
            value={settings.schedule.workStartTime}
            onChange={(v) => updateSetting('schedule.workStartTime', v)}
            compact
          />
          <TimeField
            label="Fin"
            value={settings.schedule.workEndTime}
            onChange={(v) => updateSetting('schedule.workEndTime', v)}
            compact
          />
        </div>
      </CollapsibleSection>

      {/* Assistance Level */}
      <CollapsibleSection
        icon={Sparkles}
        iconColor="text-pink-400"
        title="Niveau d'assistance"
        subtitle={getAssistanceLevelLabel(settings.assistance.level)}
        defaultOpen={false}
      >
        <AssistanceOptions settings={settings} updateSetting={updateSetting} />
      </CollapsibleSection>
    </>
  );
});

/**
 * Services Tab - Connected services and smart links
 */
const ServicesTab = memo(function ServicesTab({ settings, updateSetting }) {
  const services = [
    { key: 'spotify', name: 'Spotify', icon: Music, color: 'text-green-400 bg-green-500/20' },
    { key: 'youtube', name: 'YouTube', icon: Youtube, color: 'text-red-400 bg-red-500/20' },
    { key: 'primeVideo', name: 'Prime Video', icon: Tv, color: 'text-blue-400 bg-blue-500/20' },
  ];

  return (
    <>
      {/* Connected Services */}
      <div className="space-y-2">
        <h3 className="text-xs font-medium uppercase text-zinc-500 px-1">Services connectés</h3>
        {services.map(service => {
          const isEnabled = settings.connectedServices[service.key]?.enabled;
          const ServiceIcon = service.icon;
          return (
            <div
              key={service.key}
              className={`p-3 rounded-xl transition-all ${
                isEnabled
                  ? 'bg-zinc-800/50 border border-zinc-700/50'
                  : 'bg-zinc-800/30'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${service.color}`}>
                    <ServiceIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-zinc-200">{service.name}</span>
                    <span className="text-[10px] text-zinc-500 block">
                      {isEnabled ? 'Connecté' : 'Non connecté'}
                    </span>
                  </div>
                </div>
                <ToggleSwitch
                  checked={isEnabled}
                  onChange={() =>
                    updateSetting(`connectedServices.${service.key}.enabled`, !isEnabled)
                  }
                />
              </div>
            </div>
          );
        })}
        <p className="text-[10px] text-zinc-500 text-center mt-2">
          OAuth disponible prochainement
        </p>
      </div>

      {/* Smart Links */}
      <div className="space-y-2 mt-6">
        <h3 className="text-xs font-medium uppercase text-zinc-500 px-1">Liens intelligents</h3>

        <ToggleRow
          label="Suggestions de recettes"
          icon="🍳"
          checked={settings.smartLinks.enableRecipeSuggestions}
          onChange={() =>
            updateSetting('smartLinks.enableRecipeSuggestions', !settings.smartLinks.enableRecipeSuggestions)
          }
        />
        <ToggleRow
          label="Suggestions de lieux"
          icon="📍"
          checked={settings.smartLinks.enablePlaceSuggestions}
          onChange={() =>
            updateSetting('smartLinks.enablePlaceSuggestions', !settings.smartLinks.enablePlaceSuggestions)
          }
        />
        <ToggleRow
          label="Suggestions de playlists"
          icon="🎵"
          checked={settings.smartLinks.enablePlaylistSuggestions}
          onChange={() =>
            updateSetting('smartLinks.enablePlaylistSuggestions', !settings.smartLinks.enablePlaylistSuggestions)
          }
        />

        {/* Preferred Services */}
        <div className="mt-4 space-y-3">
          <div>
            <p className="text-[10px] font-medium uppercase text-zinc-500 mb-2">Service de musique</p>
            <div className="flex gap-2">
              {['spotify', 'youtube', 'apple'].map(service => (
                <button
                  key={service}
                  onClick={() => updateSetting('smartLinks.preferredMusicService', service)}
                  className={`flex-1 py-2 rounded-lg text-xs font-medium capitalize transition-all ${
                    settings.smartLinks.preferredMusicService === service
                      ? 'bg-green-500/30 text-green-300 border border-green-500/50'
                      : 'bg-zinc-700/30 text-zinc-400'
                  }`}
                >
                  {service === 'apple' ? 'Apple' : service}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[10px] font-medium uppercase text-zinc-500 mb-2">Service de carte</p>
            <div className="flex gap-2">
              {['google', 'apple', 'waze'].map(service => (
                <button
                  key={service}
                  onClick={() => updateSetting('smartLinks.preferredMapService', service)}
                  className={`flex-1 py-2 rounded-lg text-xs font-medium capitalize transition-all ${
                    settings.smartLinks.preferredMapService === service
                      ? 'bg-blue-500/30 text-blue-300 border border-blue-500/50'
                      : 'bg-zinc-700/30 text-zinc-400'
                  }`}
                >
                  {service === 'google' ? 'Google' : service}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

/**
 * Data Tab - Export, sync, gamification
 */
const DataTab = memo(function DataTab({
  settings,
  updateSetting,
  onExportCSV,
  onSyncGoogleTasks,
  isGoogleConnected,
  isTasksAvailable,
  isSyncing,
  onGenerateWeek,
  existingTasks,
  resetAll,
  onResetTasks,
  onResetAgendaSetup,
}) {
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showResetTasksConfirm, setShowResetTasksConfirm] = useState(false);

  return (
    <>
      {/* Auto Generation */}
      <CollapsibleSection
        icon={Calendar}
        iconColor="text-cyan-400"
        title="Génération automatique"
        subtitle={settings.autoGeneration.enabled ? 'Activée' : 'Désactivée'}
        defaultOpen={false}
      >
        <div className="space-y-4 mt-3">
          <div className="flex items-center justify-between p-3 bg-zinc-700/30 rounded-xl">
            <span className="text-sm text-zinc-200">Génération auto</span>
            <ToggleSwitch
              checked={settings.autoGeneration.enabled}
              onChange={() => updateSetting('autoGeneration.enabled', !settings.autoGeneration.enabled)}
            />
          </div>

          {settings.autoGeneration.enabled && (
            <>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'minimal', label: 'Minimal', icon: '🌱' },
                  { key: 'balanced', label: 'Équilibré', icon: '⚖️' },
                  { key: 'structured', label: 'Structuré', icon: '📋' },
                  { key: 'intense', label: 'Intense', icon: '🚀' },
                ].map(template => (
                  <button
                    key={template.key}
                    onClick={() => updateSetting('autoGeneration.template', template.key)}
                    className={`p-3 rounded-xl text-left transition-all ${
                      settings.autoGeneration.template === template.key
                        ? 'bg-cyan-500/30 border border-cyan-500/50'
                        : 'bg-zinc-700/30 hover:bg-zinc-700/50'
                    }`}
                  >
                    <span className="text-lg">{template.icon}</span>
                    <span className="text-xs font-medium text-zinc-200 block mt-1">{template.label}</span>
                  </button>
                ))}
              </div>

              <div className="space-y-2">
                <ToggleRow label="Inclure weekends" checked={settings.autoGeneration.includeWeekends}
                  onChange={() => updateSetting('autoGeneration.includeWeekends', !settings.autoGeneration.includeWeekends)} />
                <ToggleRow label="Ajouter repas" checked={settings.autoGeneration.autoAddMeals}
                  onChange={() => updateSetting('autoGeneration.autoAddMeals', !settings.autoGeneration.autoAddMeals)} />
                <ToggleRow label="Ajouter hygiène" checked={settings.autoGeneration.autoAddHygiene}
                  onChange={() => updateSetting('autoGeneration.autoAddHygiene', !settings.autoGeneration.autoAddHygiene)} />
              </div>

              {onGenerateWeek && (
                <button
                  onClick={() => onGenerateWeek(existingTasks)}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500
                    text-white font-medium text-sm flex items-center justify-center gap-2
                    hover:opacity-90 transition-all active:scale-[0.98]"
                >
                  <Zap className="w-4 h-4" />
                  Générer ma semaine
                </button>
              )}
            </>
          )}
        </div>
      </CollapsibleSection>

      {/* Gamification */}
      <CollapsibleSection
        icon={Trophy}
        iconColor="text-yellow-400"
        title="Gamification"
        subtitle={settings.gamification.enabled ? 'XP et Niveaux' : 'Désactivée'}
        defaultOpen={false}
      >
        <div className="space-y-4 mt-3">
          <div className="flex items-center justify-between p-3 bg-zinc-700/30 rounded-xl">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              <span className="text-sm text-zinc-200">Gamification</span>
            </div>
            <ToggleSwitch
              checked={settings.gamification.enabled}
              onChange={() => updateSetting('gamification.enabled', !settings.gamification.enabled)}
            />
          </div>

          {settings.gamification.enabled && (
            <>
              <div className="space-y-2">
                <ToggleRow label="Afficher XP" icon="✨" checked={settings.gamification.showXP}
                  onChange={() => updateSetting('gamification.showXP', !settings.gamification.showXP)} />
                <ToggleRow label="Afficher niveau" icon="🏆" checked={settings.gamification.showLevel}
                  onChange={() => updateSetting('gamification.showLevel', !settings.gamification.showLevel)} />
                <ToggleRow label="Streaks" icon="🔥" checked={settings.gamification.enableStreaks}
                  onChange={() => updateSetting('gamification.enableStreaks', !settings.gamification.enableStreaks)} />
                <ToggleRow label="Défis" icon="🎯" checked={settings.gamification.enableChallenges}
                  onChange={() => updateSetting('gamification.enableChallenges', !settings.gamification.enableChallenges)} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-zinc-700/30 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-emerald-400" />
                    <span className="text-xs text-zinc-300">Objectif quotidien</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={20}
                      value={settings.gamification.dailyGoal}
                      onChange={(e) => updateSetting('gamification.dailyGoal', parseInt(e.target.value) || 5)}
                      className="w-12 bg-zinc-800 text-center text-lg font-bold text-zinc-200 rounded-lg py-1 outline-none"
                    />
                    <span className="text-xs text-zinc-500">tâches</span>
                  </div>
                </div>
                <div className="p-3 bg-zinc-700/30 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="w-4 h-4 text-purple-400" />
                    <span className="text-xs text-zinc-300">Objectif hebdo</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={5}
                      max={100}
                      value={settings.gamification.weeklyGoal}
                      onChange={(e) => updateSetting('gamification.weeklyGoal', parseInt(e.target.value) || 25)}
                      className="w-12 bg-zinc-800 text-center text-lg font-bold text-zinc-200 rounded-lg py-1 outline-none"
                    />
                    <span className="text-xs text-zinc-500">tâches</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </CollapsibleSection>

      {/* Export & Sync Actions */}
      <div className="space-y-2 mt-4">
        <h3 className="text-xs font-medium uppercase text-zinc-500 px-1">Actions</h3>

        <button
          onClick={onExportCSV}
          className="w-full flex items-center gap-3 p-3 bg-zinc-800/50 hover:bg-zinc-700/50 rounded-xl transition-all active:scale-[0.98]"
        >
          <Download className="w-5 h-5 text-zinc-400" />
          <div className="text-left">
            <span className="text-sm font-medium text-zinc-200 block">Exporter la semaine</span>
            <span className="text-xs text-zinc-500">Format CSV (Excel)</span>
          </div>
        </button>

        {isGoogleConnected && isTasksAvailable && (
          <button
            onClick={onSyncGoogleTasks}
            disabled={isSyncing}
            className="w-full flex items-center gap-3 p-3 bg-zinc-800/50 hover:bg-zinc-700/50 rounded-xl transition-all disabled:opacity-50 active:scale-[0.98]"
          >
            <CheckSquare className={`w-5 h-5 text-emerald-400 ${isSyncing ? 'animate-pulse' : ''}`} />
            <div className="text-left flex-1">
              <span className="text-sm font-medium text-zinc-200 block">Sync Google Tasks</span>
              <span className="text-xs text-zinc-500">
                {isSyncing ? 'Synchronisation...' : 'Envoyer vers Google Tasks'}
              </span>
            </div>
            {isSyncing && <RefreshCw className="w-4 h-4 text-zinc-400 animate-spin" />}
          </button>
        )}

        {isGoogleConnected && (
          <DriveBackupSection />
        )}

        <button
          onClick={() => setShowResetConfirm(true)}
          className="w-full flex items-center gap-3 p-3 bg-zinc-800/30 hover:bg-zinc-700/30 rounded-xl transition-all"
        >
          <RotateCcw className="w-5 h-5 text-zinc-500" />
          <span className="text-sm text-zinc-400">Réinitialiser les paramètres</span>
        </button>

        {onResetTasks && (
          <button
            onClick={() => setShowResetTasksConfirm(true)}
            className="w-full flex items-center gap-3 p-3 bg-red-950/30 hover:bg-red-950/50 border border-red-900/30 rounded-xl transition-all"
          >
            <AlertCircle className="w-5 h-5 text-red-400" />
            <div className="text-left">
              <span className="text-sm font-medium text-red-300 block">Repartir à zéro</span>
              <span className="text-xs text-red-400/70">Supprimer toutes les tâches et reprogrammer</span>
            </div>
          </button>
        )}
      </div>

      {/* Reset Confirmation Modal */}
      <AnimatePresence>
        {showResetConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6 z-50"
            onClick={() => setShowResetConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-zinc-900 border border-zinc-700 rounded-2xl p-5 max-w-sm w-full"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-white mb-2">Réinitialiser ?</h3>
              <p className="text-sm text-zinc-400 mb-4">
                Tous les paramètres personnalisés seront perdus.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowResetConfirm(false)}
                  className="flex-1 py-2.5 bg-zinc-800 text-zinc-300 rounded-xl font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    resetAll();
                    setShowResetConfirm(false);
                  }}
                  className="flex-1 py-2.5 bg-red-500 text-white rounded-xl font-medium"
                >
                  Réinitialiser
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reset Tasks Confirmation Modal */}
      <AnimatePresence>
        {showResetTasksConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6 z-50"
            onClick={() => setShowResetTasksConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-zinc-900 border border-red-900/50 rounded-2xl p-5 max-w-sm w-full"
              onClick={e => e.stopPropagation()}
            >
              <div className="w-12 h-12 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2 text-center">Repartir à zéro ?</h3>
              <p className="text-sm text-zinc-400 mb-4 text-center">
                Toutes tes tâches et événements seront supprimés. Tu pourras ensuite reprogrammer ta semaine depuis l'agenda.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowResetTasksConfirm(false)}
                  className="flex-1 py-2.5 bg-zinc-800 text-zinc-300 rounded-xl font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    onResetTasks?.();
                    onResetAgendaSetup?.();
                    setShowResetTasksConfirm(false);
                  }}
                  className="flex-1 py-2.5 bg-red-500 text-white rounded-xl font-medium"
                >
                  Tout supprimer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
});

// ================================
// Diagnostic Tab
// ============================================================
// A11Y TAB
// ============================================================

const A11yTab = memo(function A11yTab({ a11y }) {
  const { settings, update, reset } = a11y;

  return (
    <div className="p-4 space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-200">Accessibilité</h3>
        <button onClick={reset} className="text-[10px] text-zinc-600 hover:text-zinc-400 transition-colors">
          Réinitialiser
        </button>
      </div>

      {/* Vision */}
      <div className="space-y-2">
        <p className="text-[10px] font-semibold uppercase text-zinc-600 tracking-wider">Vision</p>

        <A11yToggle
          label="Mode contraste élevé"
          description="Augmente le contraste des textes et bordures"
          checked={settings.highContrast}
          onChange={v => update('highContrast', v)}
        />
        <A11yToggle
          label="Mode daltonien"
          description="Remplace rouge/vert par orange/bleu"
          checked={settings.colorblind}
          onChange={v => update('colorblind', v)}
        />
      </div>

      {/* Lecture */}
      <div className="space-y-2">
        <p className="text-[10px] font-semibold uppercase text-zinc-600 tracking-wider">Lecture</p>

        <A11yToggle
          label="Police dyslexie (Lexend)"
          description="Améliore la lisibilité pour la dyslexie"
          checked={settings.dyslexiaFont}
          onChange={v => update('dyslexiaFont', v)}
        />

        <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
          <div>
            <p className="text-sm font-medium text-zinc-200">Taille du texte</p>
            <p className="text-xs text-zinc-500">Agrandit tous les textes de l'app</p>
          </div>
          <div className="flex gap-1">
            {[['normal', 'A'], ['large', 'A+'], ['xl', 'A++']].map(([size, label]) => (
              <button
                key={size}
                onClick={() => update('textSize', size)}
                className={`px-2.5 py-1 rounded text-xs font-bold transition-all ${
                  settings.textSize === size
                    ? 'bg-blue-500/30 border border-blue-500/60 text-blue-300'
                    : 'bg-zinc-700/40 border border-zinc-700 text-zinc-500 hover:border-zinc-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Motricité / Interaction */}
      <div className="space-y-2">
        <p className="text-[10px] font-semibold uppercase text-zinc-600 tracking-wider">Interaction</p>

        <A11yToggle
          label="Grandes cibles tactiles"
          description="Augmente la taille minimale des boutons (44×44px)"
          checked={settings.largeTouchTargets}
          onChange={v => update('largeTouchTargets', v)}
        />
        <A11yToggle
          label="Désactiver les animations"
          description="Supprime toutes les transitions et animations"
          checked={settings.disableAnimations}
          onChange={v => update('disableAnimations', v)}
        />
      </div>

      <p className="text-[10px] text-zinc-700 text-center">
        Les réglages s'appliquent immédiatement et sont sauvegardés localement.
      </p>
    </div>
  );
});

const A11yToggle = memo(function A11yToggle({ label, description, checked, onChange }) {
  return (
    <label className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg cursor-pointer hover:bg-zinc-800/50 transition-all">
      <div className="flex-1 min-w-0 mr-3">
        <p className="text-sm font-medium text-zinc-200">{label}</p>
        <p className="text-xs text-zinc-500">{description}</p>
      </div>
      <div className="relative flex-shrink-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          className="sr-only peer"
        />
        <div className="w-11 h-6 bg-zinc-700 rounded-full peer peer-checked:bg-blue-500 transition-colors" />
        <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow peer-checked:translate-x-5 transition-transform" />
      </div>
    </label>
  );
});

// ================================

const SEVERITY_COLOR = {
  user_facing: 'text-amber-400',
  critical:    'text-red-400',
  background:  'text-zinc-500',
};

const TYPE_LABEL = {
  NETWORK:    'Réseau',
  AUTH:       'Auth',
  API_QUOTA:  'Quota',
  FIREBASE:   'Firebase',
  VALIDATION: 'Validation',
  UNKNOWN:    'Inconnu',
};

const DiagTab = memo(function DiagTab() {
  const [errors, setErrors] = useState(() => getLastErrors(10));
  const [copied, setCopied] = useState(false);

  // Refresh the list whenever a new error is captured
  useEffect(() => {
    const unsub = subscribe(() => setErrors(getLastErrors(10)));
    return unsub;
  }, []);

  const handleCopy = useCallback(() => {
    const text = errors
      .map(e => `[${e.timestamp}] [${e.type}] ${e.context}: ${e.message}`)
      .join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [errors]);

  const handleClear = useCallback(() => {
    clearErrors();
    setErrors([]);
  }, []);

  return (
    <>
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
          Dernières erreurs ({errors.length})
        </h3>
        <div className="flex gap-2">
          <button
            onClick={handleCopy}
            disabled={errors.length === 0}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium
              bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg transition-colors
              disabled:opacity-40"
          >
            {copied ? <CheckCheck className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? 'Copié' : 'Copier'}
          </button>
          <button
            onClick={handleClear}
            disabled={errors.length === 0}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium
              bg-zinc-800 hover:bg-red-900/40 text-zinc-300 hover:text-red-300 rounded-lg
              transition-colors disabled:opacity-40"
          >
            <Trash2 className="w-3 h-3" />
            Vider
          </button>
        </div>
      </div>

      {errors.length === 0 ? (
        <div className="text-center py-10 text-zinc-600 text-sm">
          Aucune erreur enregistrée
        </div>
      ) : (
        <div className="space-y-2">
          {errors.map(entry => (
            <div
              key={entry.id}
              className="p-3 bg-zinc-800/50 rounded-xl space-y-1 text-xs"
            >
              <div className="flex items-center justify-between gap-2">
                <span className={`font-semibold ${SEVERITY_COLOR[entry.severity] ?? 'text-zinc-400'}`}>
                  {TYPE_LABEL[entry.type] ?? entry.type}
                </span>
                <span className="text-zinc-600 font-mono text-[10px] shrink-0">
                  {new Date(entry.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              </div>
              {entry.context && (
                <p className="text-zinc-500 text-[10px]">{entry.context}</p>
              )}
              <p className="text-zinc-300 break-words leading-snug">{entry.message}</p>
            </div>
          ))}
        </div>
      )}

      <p className="text-[10px] text-zinc-600 text-center mt-4">
        Max 50 erreurs conservées · Non persistées
      </p>
    </>
  );
});

// ================================
// Shared Sub-Components
// ================================

/**
 * Collapsible Section
 */
const CollapsibleSection = memo(function CollapsibleSection({
  icon: Icon,
  iconColor,
  title,
  subtitle,
  defaultOpen = false,
  children,
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={`rounded-xl overflow-hidden transition-all ${
      isOpen ? 'bg-zinc-800/50 border border-zinc-700/50' : 'bg-zinc-800/30'
    }`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4"
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg bg-zinc-700/50 ${iconColor}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="text-left">
            <span className="text-sm font-medium text-zinc-200 block">{title}</span>
            <span className="text-xs text-zinc-500">{subtitle}</span>
          </div>
        </div>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown className="w-5 h-5 text-zinc-500" />
        </motion.div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 border-t border-zinc-700/30">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

/**
 * Setting Toggle
 */
const SettingToggle = memo(function SettingToggle({
  icon: Icon,
  iconActive,
  label,
  description,
  checked,
  onChange,
}) {
  return (
    <label className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-xl cursor-pointer hover:bg-zinc-800/50 transition-all">
      <div className="flex items-center gap-3">
        <Icon className={`w-5 h-5 ${iconActive ? 'text-zinc-300' : 'text-zinc-500'}`} />
        <div>
          <span className="text-sm font-medium text-zinc-200">{label}</span>
          <span className="text-xs text-zinc-500 block">{description}</span>
        </div>
      </div>
      <div className="relative">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="sr-only peer"
        />
        <div className="w-11 h-6 bg-zinc-700 rounded-full peer peer-checked:bg-[var(--theme-accent,#3b82f6)] transition-colors" />
        <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow peer-checked:translate-x-5 transition-transform" />
      </div>
    </label>
  );
});

/**
 * Toggle Row
 */
const ToggleRow = memo(function ToggleRow({ label, icon, checked, onChange }) {
  return (
    <button
      onClick={onChange}
      className="w-full flex items-center justify-between p-2.5 bg-zinc-700/30 hover:bg-zinc-700/50 rounded-lg transition-all"
    >
      <div className="flex items-center gap-2">
        {icon && <span className="text-sm">{icon}</span>}
        <span className="text-xs text-zinc-300">{label}</span>
      </div>
      <ToggleSwitch checked={checked} onChange={onChange} />
    </button>
  );
});

/**
 * Toggle Switch
 */
const ToggleSwitch = memo(function ToggleSwitch({ checked, onChange }) {
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onChange?.(); }}
      className="relative cursor-pointer"
    >
      <div className={`w-10 h-5 rounded-full transition-colors ${checked ? 'bg-emerald-500' : 'bg-zinc-600'}`} />
      <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
    </div>
  );
});

/**
 * Input Field
 */
const InputField = memo(function InputField({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="text-[10px] font-medium uppercase text-zinc-500 block mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2
          text-sm text-zinc-200 placeholder-zinc-600 outline-none
          focus:border-zinc-600 transition-colors"
      />
    </div>
  );
});

/**
 * Time Field
 */
const TimeField = memo(function TimeField({ icon: Icon, iconColor, label, value, onChange, compact }) {
  return (
    <div className={compact ? '' : 'p-3 bg-zinc-700/30 rounded-xl'}>
      <div className={`flex items-center gap-2 ${compact ? 'mb-1' : 'mb-2'}`}>
        {Icon && <Icon className={`w-4 h-4 ${iconColor}`} />}
        <span className={`text-${compact ? '[10px]' : 'xs'} text-zinc-400`}>{label}</span>
      </div>
      <input
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg
          ${compact ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'}
          text-zinc-200 outline-none focus:border-zinc-600 transition-colors`}
      />
    </div>
  );
});

/**
 * Assistance Options
 */
const AssistanceOptions = memo(function AssistanceOptions({ settings, updateSetting }) {
  const levels = [
    { key: 'minimal', label: 'Minimal', icon: '🌙' },
    { key: 'normal', label: 'Normal', icon: '☀️' },
    { key: 'maximum', label: 'Maximum', icon: '🌟' },
  ];

  return (
    <div className="space-y-4 mt-3">
      <div className="grid grid-cols-3 gap-2">
        {levels.map(level => (
          <button
            key={level.key}
            onClick={() => updateSetting('assistance.level', level.key)}
            className={`p-3 rounded-xl text-center transition-all ${
              settings.assistance.level === level.key
                ? 'bg-purple-500/30 border border-purple-500/50'
                : 'bg-zinc-700/30 hover:bg-zinc-700/50'
            }`}
          >
            <span className="text-lg block mb-1">{level.icon}</span>
            <span className="text-xs font-medium text-zinc-200">{level.label}</span>
          </button>
        ))}
      </div>

      <ToggleRow
        label="Vibrations tactiles"
        checked={settings.assistance.enableHapticFeedback}
        onChange={() => updateSetting('assistance.enableHapticFeedback', !settings.assistance.enableHapticFeedback)}
      />

      <div>
        <p className="text-[10px] font-medium uppercase text-zinc-500 mb-2">Encouragements</p>
        <div className="flex gap-2">
          {['low', 'medium', 'high'].map(freq => (
            <button
              key={freq}
              onClick={() => updateSetting('assistance.encouragementFrequency', freq)}
              className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                settings.assistance.encouragementFrequency === freq
                  ? 'bg-green-500/30 text-green-300 border border-green-500/50'
                  : 'bg-zinc-700/30 text-zinc-400'
              }`}
            >
              {freq === 'low' ? 'Peu' : freq === 'medium' ? 'Moyen' : 'Beaucoup'}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
});

// Helper
function getAssistanceLevelLabel(level) {
  const labels = { minimal: 'Minimal', normal: 'Normal', maximum: 'Maximum' };
  return labels[level] || 'Normal';
}

// ─── Google Drive Backup/Restore ──────────────────────────────────────────
const DriveBackupSection = memo(function DriveBackupSection() {
  const [status, setStatus] = useState('idle'); // idle | saving | restoring | done | error
  const [message, setMessage] = useState('');

  const handleBackup = useCallback(async () => {
    setStatus('saving');
    setMessage('');
    try {
      const { default: driveService } = await import('../../../services/googleDrive');

      // Collect all localStorage data
      const backup = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.startsWith('adhd-planner') || key.startsWith('adhd_')) {
          backup[key] = localStorage.getItem(key);
        }
      }

      await driveService.initAppFolder();
      await driveService.saveTasks(backup);
      setStatus('done');
      setMessage('Sauvegarde terminee !');
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Erreur sauvegarde');
    }
  }, []);

  const handleRestore = useCallback(async () => {
    setStatus('restoring');
    setMessage('');
    try {
      const { default: driveService } = await import('../../../services/googleDrive');
      await driveService.initAppFolder();
      const data = await driveService.loadTasks();

      if (!data || !data.tasks) {
        setStatus('error');
        setMessage('Aucune sauvegarde trouvee');
        return;
      }

      // Restore all keys
      const backup = data.tasks;
      Object.entries(backup).forEach(([key, value]) => {
        if (key.startsWith('adhd-planner') || key.startsWith('adhd_')) {
          localStorage.setItem(key, value);
        }
      });

      setStatus('done');
      setMessage('Restauration terminee ! Rechargement...');
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Erreur restauration');
    }
  }, []);

  return (
    <div className="space-y-2">
      <button
        onClick={handleBackup}
        disabled={status === 'saving' || status === 'restoring'}
        className="w-full flex items-center gap-3 p-3 bg-zinc-800/50 hover:bg-zinc-700/50 rounded-xl transition-all disabled:opacity-50 active:scale-[0.98]"
      >
        <Cloud className="w-5 h-5 text-blue-400" />
        <div className="text-left">
          <span className="text-sm font-medium text-zinc-200 block">
            {status === 'saving' ? 'Sauvegarde en cours...' : 'Sauvegarder sur Google Drive'}
          </span>
          <span className="text-xs text-zinc-500">Toutes les donnees de l'app</span>
        </div>
      </button>

      <button
        onClick={handleRestore}
        disabled={status === 'saving' || status === 'restoring'}
        className="w-full flex items-center gap-3 p-3 bg-zinc-800/50 hover:bg-zinc-700/50 rounded-xl transition-all disabled:opacity-50 active:scale-[0.98]"
      >
        <Download className="w-5 h-5 text-emerald-400" />
        <div className="text-left">
          <span className="text-sm font-medium text-zinc-200 block">
            {status === 'restoring' ? 'Restauration en cours...' : 'Restaurer depuis Google Drive'}
          </span>
          <span className="text-xs text-zinc-500">Recuperer une sauvegarde precedente</span>
        </div>
      </button>

      {message && (
        <p className={`text-xs px-2 ${status === 'error' ? 'text-red-400' : 'text-emerald-400'}`}>
          {message}
        </p>
      )}
    </div>
  );
});

export default UnifiedSettingsSheet;
